import { useEffect, useState } from "react";
import { AdminUsersAPI } from "../../api/adminUsers";
import { AdminRbacAPI } from "../../api/adminRbac";
import { SchoolsAPI, DepartmentsAPI } from "../../api/lookups";

export default function UserAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [saving, setSaving] = useState(false);

  // Password modal state
  const [passwordModalFor, setPasswordModalFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Add / edit users modal
  const [editModalFor, setEditModalFor] = useState(null); // null = closed, {} = new, UserObj = edit
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editError, setEditError] = useState("");

  // Roles modal
  const [rolesModalFor, setRolesModalFor] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState("");
  const [rolesSaving, setRolesSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadUsers() {
      setLoading(true);
      setError("");
      try {
        const data = await AdminUsersAPI.list();
        if (!cancelled) {
          setUsers(data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err.message || "Failed to load users");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  const openPasswordModal = (member) => {
    setPasswordModalFor(member);
    setNewPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const closePasswordModal = () => {
    setPasswordModalFor(null);
    setNewPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!passwordModalFor) return;
    setSaving(true);
    setPasswordError("");
    setPasswordSuccess("");

    try {
      await AdminUsersAPI.setPassword(passwordModalFor.id, newPassword);
      setPasswordSuccess("Password updated");

      setUsers((prev) =>
        prev.map((s) =>
          s.id === passwordModalFor.id ? { ...s, has_password: true } : s
        )
      );
    } catch (err) {
      console.error(err);
      setPasswordError(err.message || "Failed to set password");
    } finally {
      setSaving(false);
    }
  };

  const openAddUserModal = () => {
    setEditModalFor({ isNew: true });
    setEditName("");
    setEditEmail("");
    setEditPassword("");
    setEditError("");
  };

  const openEditUserModal = (member) => {
    setEditModalFor(member);
    setEditName(member.name || "");
    setEditEmail(member.email || "");
    setEditPassword("");
    setEditError("");
  };

  const closeEditModal = () => {
    setEditModalFor(null);
    setEditError("");
    setEditPassword("");
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    setEditError("");

    try {
      if (editModalFor?.isNew) {
        const payload = {
          name: editName,
          email: editEmail,
        };
        if (editPassword) {
          payload.password = editPassword;
        }
        const created = await AdminUsersAPI.create(payload);
        setUsers((prev) =>
          [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
        );
      } else {
        const payload = {
          name: editName,
          email: editEmail,
        };
        const updated = await AdminUsersAPI.update(editModalFor.id, payload);
        setUsers((prev) =>
          prev
            .map((s) => (s.id === updated.id ? updated : s))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }

      closeEditModal();
    } catch (err) {
      console.error(err);
      setEditError(err.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (member) => {
    if (!window.confirm(`Delete ${member.name}? This will archive their account.`)) {
      return;
    }

    try {
      await AdminUsersAPI.remove(member.id);
      setUsers((prev) => prev.filter((s) => s.id !== member.id));
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to delete users");
    }
  };

  const openRolesModal = async (member) => {
    setRolesModalFor(member);
    setRolesLoading(true);
    setRolesError("");
    setAllRoles([]);
    setSchools([]);
    setDepartments([]);
    setRoleAssignments([]);

    try {
      const [roles, userAssignments, schoolRows, departmentRows] = await Promise.all([
        AdminRbacAPI.listRoles(),
        AdminUsersAPI.getRolesForUser(member.id),
        SchoolsAPI.list(),
        DepartmentsAPI.list(),
      ]);

      setAllRoles(roles || []);
      setSchools(schoolRows || []);
      setDepartments(departmentRows || []);
      const grouped = {};

      (userAssignments || []).forEach((a) => {
        const roleId = a.id ? String(a.id) : "";
        const scopeType = a.scope_type || "global";
        const key = `${roleId}__${scopeType}`;

        if (!grouped[key]) {
          grouped[key] = {
            roleId,
            scope_type: scopeType,
            school_ids: [],
            department_ids: [],
          };
        }

        if (scopeType === "school" && a.school_id) {
          const schoolId = String(a.school_id);
          if (!grouped[key].school_ids.includes(schoolId)) {
            grouped[key].school_ids.push(schoolId);
          }
        }

        if (a.department_id) {
          const departmentId = String(a.department_id);
          if (!grouped[key].department_ids.includes(departmentId)) {
            grouped[key].department_ids.push(departmentId);
          }
        }
      });

      setRoleAssignments(Object.values(grouped));
    } catch (err) {
      console.error(err);
      setRolesError(err.message || "Failed to load role assignments");
    } finally {
      setRolesLoading(false);
    }
  };

  const closeRolesModal = () => {
    setRolesModalFor(null);
    setAllRoles([]);
    setSchools([]);
    setRoleAssignments([]);
    setRolesError("");
    setRolesSaving(false);
  };

  const addAssignmentRow = () => {
    setRoleAssignments((prev) => [
      ...prev,
      {
        roleId: "",
        scope_type: "global",
        school_ids: [],
        department_ids: [],
      },
    ]);
  };

    const updateAssignmentRow = (index, patch) => {
      setRoleAssignments((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;

          const next = { ...row, ...patch };

          if (patch.scope_type === "global") {
            next.school_ids = [];
          }

          if (!Array.isArray(next.school_ids)) {
            next.school_ids = [];
          }

          if (!Array.isArray(next.department_ids)) {
            next.department_ids = [];
          }

          return next;
        })
      );
    };

    const toggleAssignmentSchool = (index, schoolId) => {
      setRoleAssignments((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;

          const schoolIds = Array.isArray(row.school_ids) ? row.school_ids : [];
          const sid = String(schoolId);

          const nextSchoolIds = schoolIds.includes(sid)
            ? schoolIds.filter((id) => id !== sid)
            : [...schoolIds, sid];

          return {
            ...row,
            school_ids: nextSchoolIds,
          };
        })
      );
    };

    const toggleAssignmentDepartment = (index, departmentId) => {
      setRoleAssignments((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;

          const departmentIds = Array.isArray(row.department_ids)
            ? row.department_ids
            : [];
          const did = String(departmentId);

          const nextDepartmentIds = departmentIds.includes(did)
            ? departmentIds.filter((id) => id !== did)
            : [...departmentIds, did];

          return {
            ...row,
            department_ids: nextDepartmentIds,
          };
        })
      );
    };

  const removeAssignmentRow = (index) => {
    setRoleAssignments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveRoles = async (e) => {
    e.preventDefault();
    if (!rolesModalFor) return;

    setRolesSaving(true);
    setRolesError("");

    try {
      const cleanedAssignments = roleAssignments
        .filter((a) => a.roleId && a.scope_type)
        .flatMap((a) => {
          const departmentIds = Array.isArray(a.department_ids)
            ? a.department_ids
            : [];

          if (a.scope_type === "global") {
            if (departmentIds.length === 0) {
              return [
                {
                  roleId: Number(a.roleId),
                  scope_type: "global",
                  school_id: null,
                  department_id: null,
                },
              ];
            }

            return departmentIds.map((departmentId) => ({
              roleId: Number(a.roleId),
              scope_type: "global",
              school_id: null,
              department_id: departmentId,
            }));
          }

          const schoolIds = Array.isArray(a.school_ids) ? a.school_ids : [];

          if (departmentIds.length === 0) {
            return schoolIds.map((schoolId) => ({
              roleId: Number(a.roleId),
              scope_type: "school",
              school_id: Number(schoolId),
              department_id: null,
            }));
          }

          return schoolIds.flatMap((schoolId) =>
            departmentIds.map((departmentId) => ({
              roleId: Number(a.roleId),
              scope_type: "school",
              school_id: Number(schoolId),
              department_id: departmentId,
            }))
          );
        });

      const invalidSchoolScoped = roleAssignments.some(
        (a) =>
          a.roleId &&
          a.scope_type === "school" &&
          (!Array.isArray(a.school_ids) || a.school_ids.length === 0)
      );

      if (invalidSchoolScoped) {
        setRolesError("Every school-scoped role must have a school selected.");
        setRolesSaving(false);
        return;
      }

      await AdminUsersAPI.setRolesForUser(
        rolesModalFor.id,
        cleanedAssignments
      );

      closeRolesModal();
    } catch (err) {
      console.error(err);
      setRolesError(err.message || "Failed to update role assignments");
      setRolesSaving(false);
    }
  };

  const roleNameFor = (roleId) => {
    const role = allRoles.find((r) => String(r.id) === String(roleId));
    return role ? role.name : "";
  };

  const schoolNameFor = (schoolId) => {
    const school = schools.find((s) => String(s.id) === String(schoolId));
    return school ? school.name : "";
  };

  const filteredUsers = users.filter((member) => {
    const q = search.trim().toLowerCase();

    if (!q) return true;

    return (
      (member.name || "").toLowerCase().includes(q) ||
      (member.email || "").toLowerCase().includes(q)
    );
  });

  if (loading) return <p>Loading users…</p>;
  if (error && !editModalFor && !passwordModalFor && !rolesModalFor) {
    return <p className="text-red-600">{error}</p>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">User Admin</h1>
          <p className="text-sm text-gray-600">
            Manage users, passwords, and role assignments.
          </p>
        </div>
        <button
          onClick={openAddUserModal}
          className="px-3 py-2 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800"
        >
          + Add users
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users by name or email..."
          className="w-full max-w-md border rounded px-3 py-2 text-sm"
        />
      </div>

      <div className="overflow-x-auto border rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Email</th>
              <th className="px-3 py-2 text-left font-semibold">Role</th>
              <th className="px-3 py-2 text-left font-semibold">
                Local password
              </th>
              <th className="px-3 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((member) => (
              <tr key={member.id} className="border-t">
                <td className="px-3 py-2">{member.name}</td>
                <td className="px-3 py-2">{member.email}</td>
                <td className="px-3 py-2">{member.role}</td>
                <td className="px-3 py-2">
                  {member.has_password ? "Configured" : "Not set"}
                </td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button
                    onClick={() => openRolesModal(member)}
                    className="inline-flex items-center px-2 py-1 border rounded text-xs hover:bg-gray-50"
                  >
                    Roles
                  </button>
                  <button
                    onClick={() => openPasswordModal(member)}
                    className="inline-flex items-center px-2 py-1 border rounded text-xs hover:bg-gray-50"
                  >
                    Password
                  </button>
                  <button
                    onClick={() => openEditUserModal(member)}
                    className="inline-flex items-center px-2 py-1 border rounded text-xs hover:bg-gray-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteUser(member)}
                    className="inline-flex items-center px-2 py-1 border rounded text-xs text-red-700 border-red-300 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {filteredUsers.length === 0 && (
              <tr>
                <td
                  className="px-3 py-4 text-center text-gray-500"
                  colSpan={5}
                >
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {passwordModalFor && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h2 className="text-lg font-semibold mb-2">
              Set password for {passwordModalFor.name}
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              {passwordModalFor.email}
            </p>

            <form onSubmit={handleSavePassword} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  New password
                </label>
                <input
                  type="password"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 8 characters. Consider using a temporary password and
                  asking the user to change it.
                </p>
              </div>

              {passwordError && (
                <p className="text-sm text-red-600">{passwordError}</p>
              )}
              {passwordSuccess && (
                <p className="text-sm text-green-600">{passwordSuccess}</p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="px-3 py-1 text-sm border rounded"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editModalFor && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h2 className="text-lg font-semibold mb-2">
              {editModalFor.isNew ? "Add user" : "Edit user"}
            </h2>

            <form onSubmit={handleSaveUser} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Name
                </label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  required
                />
              </div>

              {editModalFor.isNew && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Initial password (optional)
                  </label>
                  <input
                    type="password"
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    minLength={8}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to require Microsoft 365 login initially.
                  </p>
                </div>
              )}

              {editError && (
                <p className="text-sm text-red-600">{editError}</p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-3 py-1 text-sm border rounded"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {rolesModalFor && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-4 max-h-[85vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-2">
              Role assignments for {rolesModalFor.name}
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              {rolesModalFor.email}
            </p>

            {rolesLoading ? (
              <p>Loading role assignments…</p>
            ) : rolesError ? (
              <p className="text-sm text-red-600 mb-2">{rolesError}</p>
            ) : (
              <form onSubmit={handleSaveRoles} className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Add one or more role assignments. Use global for trust-wide
                    access, or school to scope a role to a specific school.
                  </div>
                  <button
                    type="button"
                    onClick={addAssignmentRow}
                    className="px-3 py-1 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800"
                  >
                    + Add assignment
                  </button>
                </div>

                {roleAssignments.length === 0 ? (
                  <div className="border rounded p-4 text-sm text-gray-500">
                    No role assignments yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {roleAssignments.map((assignment, index) => (
                      <div
                        key={index}
                        className="border rounded p-4 grid grid-cols-1 lg:grid-cols-4 gap-4 items-start"
                      >
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Role
                          </label>
                          <select
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={assignment.roleId}
                            onChange={(e) =>
                              updateAssignmentRow(index, {
                                roleId: e.target.value,
                              })
                            }
                          >
                            <option value="">Select role</option>
                            {allRoles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Scope
                          </label>
                          <select
                            className="w-full border rounded px-2 py-1 text-sm"
                            value={assignment.scope_type}
                            onChange={(e) =>
                              updateAssignmentRow(index, {
                                scope_type: e.target.value,
                              })
                            }
                          >
                            <option value="global">Global</option>
                            <option value="school">School</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Schools
                          </label>

                          {assignment.scope_type !== "school" ? (
                            <div className="w-full border rounded px-2 py-2 text-sm text-gray-400 bg-gray-50">
                              Not applicable
                            </div>
                          ) : (
                            <div className="w-full border rounded px-3 py-2 text-sm max-h-40 overflow-y-auto space-y-2">                              {schools.map((school) => {
                                const checked =
                                  Array.isArray(assignment.school_ids) &&
                                  assignment.school_ids.includes(String(school.id));

                                return (
                                  <label
                                    key={school.id}
                                    className="flex items-center gap-2"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleAssignmentSchool(index, school.id)}
                                    />
                                    <span>{school.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Departments
                          </label>

                          <div className="w-full border rounded px-3 py-2 text-sm max-h-40 overflow-y-auto space-y-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={
                                  !Array.isArray(assignment.department_ids) ||
                                  assignment.department_ids.length === 0
                                }
                                onChange={() =>
                                  updateAssignmentRow(index, {
                                    department_ids: [],
                                  })
                                }
                              />
                              <span>All departments</span>
                            </label>

                            {departments.map((department) => {
                              const checked =
                                Array.isArray(assignment.department_ids) &&
                                assignment.department_ids.includes(String(department.id));

                              return (
                                <label
                                  key={department.id}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      toggleAssignmentDepartment(index, department.id)
                                    }
                                  />
                                  <span>{department.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="lg:col-span-4 flex justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => removeAssignmentRow(index)}
                            className="px-3 py-1 text-sm border rounded text-red-700 border-red-300 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>

                        {assignment.roleId && (
                          <div className="lg:col-span-4 text-xs text-gray-500 pt-1">
                            {roleNameFor(assignment.roleId)}
                            {assignment.scope_type === "global"
                              ? " @ Global"
                              : assignment.scope_type === "school"
                              ? ` @ ${
                                  (assignment.school_ids || [])
                                    .map((id) => schoolNameFor(id))
                                    .filter(Boolean)
                                    .join(", ") || "No schools selected"
                                }`
                              : ""}
                            {Array.isArray(assignment.department_ids) &&
                            assignment.department_ids.length > 0
                              ? ` @ ${
                                  assignment.department_ids
                                    .map(
                                      (id) =>
                                        departments.find((d) => String(d.id) === String(id))?.name
                                    )
                                    .filter(Boolean)
                                    .join(", ")
                                }`
                              : " @ All departments"}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeRolesModal}
                    className="px-3 py-1 text-sm border rounded"
                    disabled={rolesSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1 text-sm border rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                    disabled={rolesSaving}
                  >
                    {rolesSaving ? "Saving…" : "Save roles"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}